import React, { useState } from 'react';
import axios from 'axios';
import { MessageSquarePlus, X, Send, CheckCircle2, Camera, Trash2 } from 'lucide-react';
import { API_URL } from '../../constants/api';
import { trackFeatureUsage } from '../../utils/monitoring';
import { getRecentErrors } from '../../utils/errorBuffer';
import { getAppVersion } from '../../utils/version';

const API = `${API_URL}/api`;

const feedbackTypes = [
  { id: 'bug', label: 'Bug' },
  { id: 'confusion', label: 'Confusing' },
  { id: 'idea', label: 'Idea' },
  { id: 'praise', label: 'Praise' },
];

const severities = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
  { id: 'blocker', label: 'Blocker' },
];

// Lightweight inline browser detection — avoids pulling a UA parser library.
const detectBrowser = () => {
  const ua = navigator.userAgent || '';
  const match = (re) => (ua.match(re) || [])[1];
  let name = 'unknown';
  let version = '';
  if (/Edg\//.test(ua)) { name = 'Edge'; version = match(/Edg\/([\d.]+)/); }
  else if (/OPR\//.test(ua)) { name = 'Opera'; version = match(/OPR\/([\d.]+)/); }
  else if (/Firefox\//.test(ua)) { name = 'Firefox'; version = match(/Firefox\/([\d.]+)/); }
  else if (/Chrome\//.test(ua)) { name = 'Chrome'; version = match(/Chrome\/([\d.]+)/); }
  else if (/Safari\//.test(ua) && /Version\//.test(ua)) { name = 'Safari'; version = match(/Version\/([\d.]+)/); }
  let os = 'unknown';
  if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';
  return { name, version: version || '', os };
};

const FeedbackWidget = ({ currentUser, currentTab }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState('bug');
  const [severity, setSeverity] = useState('medium');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [screenshot, setScreenshot] = useState(null); // base64 data URL or null
  const [screenshotting, setScreenshotting] = useState(false);

  const resetForm = () => {
    setType('bug');
    setSeverity('medium');
    setMessage('');
    setError('');
    setScreenshot(null);
  };

  const handleCaptureScreenshot = async () => {
    if (screenshotting) return;
    setScreenshotting(true);
    setError('');
    try {
      // Lazy-load html2canvas only when the user clicks — keeps it out of the main bundle.
      const html2canvas = (await import('html2canvas')).default;
      // Briefly hide the modal so the screenshot captures the underlying UI, not the form itself.
      setIsOpen(false);
      await new Promise((r) => setTimeout(r, 80));
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        scale: Math.min(1, 1200 / Math.max(window.innerWidth, 1)), // cap width ~1200px
        backgroundColor: null,
      });
      setIsOpen(true);
      // JPEG @ 0.6 keeps payload small; full-page screenshots typically land under 500KB.
      const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
      setScreenshot(dataUrl);
    } catch (err) {
      setIsOpen(true);
      console.error('Screenshot capture failed:', err);
      setError('Could not capture screenshot. You can still send feedback without one.');
    } finally {
      setScreenshotting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSubmitted(false);
    resetForm();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = message.trim();
    if (trimmed.length < 5) {
      setError('Please add a little more detail.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const browser = detectBrowser();
      await axios.post(`${API}/feedback`, {
        type,
        severity,
        message: trimmed,
        page: currentTab || 'unknown',
        screenshot: screenshot || null,
        metadata: {
          path: window.location.pathname,
          search: window.location.search,
          href: window.location.href,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          screen: `${window.screen?.width || ''}x${window.screen?.height || ''}`,
          user_agent: window.navigator.userAgent,
          browser: `${browser.name}${browser.version ? ' ' + browser.version : ''}`,
          os: browser.os,
          language: window.navigator.language,
          app_version: getAppVersion(),
          referrer: document.referrer || '',
          console_errors: getRecentErrors(),
        },
      });
      trackFeatureUsage('Feedback', 'submit');
      setSubmitted(true);
      window.dispatchEvent(new CustomEvent('feedbackSubmitted'));
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser) return null;

  return (
    <>
      <button
        type="button"
        data-fp-fab="feedback"
        onClick={() => setIsOpen(true)}
        className="fixed left-4 bottom-[calc(48px_+_env(safe-area-inset-bottom,_0px)_+_14px)] md:left-auto md:right-6 md:bottom-[158px] z-[9997] rounded-full px-4 py-3 flex items-center gap-2 text-sm font-semibold transition-all hover:scale-105 active:scale-95 shadow-xl"
        style={{
          background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
          color: '#ffffff',
          boxShadow: '0 12px 30px rgba(20, 184, 166, 0.24)',
        }}
        title="Send beta feedback"
      >
        <MessageSquarePlus className="w-4 h-4" />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center px-3 py-4" style={{ backgroundColor: 'rgba(2, 6, 23, 0.62)' }}>
          <div
            className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl"
            style={{ backgroundColor: 'var(--card-bg-solid, #111820)', border: '1px solid var(--card-border, #1e293b)' }}
          >
            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--card-border, #1e293b)' }}>
              <div>
                <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Send beta feedback</h2>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Tell us what broke, confused you, or felt useful.</p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="p-2 rounded-lg transition-colors"
                style={{ color: 'var(--text-muted)' }}
                aria-label="Close feedback form"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {submitted ? (
              <div className="p-6 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: '#22c55e' }} />
                <h3 className="text-base font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Feedback received</h3>
                <p className="text-sm mb-5" style={{ color: 'var(--text-muted)' }}>Thanks — this is now in the admin feedback inbox.</p>
                <button type="button" onClick={handleClose} className="btn-calming-primary px-5 py-2 rounded-lg">
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>Type</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {feedbackTypes.map(item => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setType(item.id)}
                        className="px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                        style={{
                          backgroundColor: type === item.id ? 'rgba(20,184,166,0.18)' : 'var(--bg-surface, #0f1923)',
                          color: type === item.id ? 'var(--accent-primary, #2dd4bf)' : 'var(--text-secondary)',
                          border: type === item.id ? '1px solid rgba(20,184,166,0.38)' : '1px solid var(--card-border, #1e293b)',
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>Severity</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {severities.map(item => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setSeverity(item.id)}
                        className="px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                        style={{
                          backgroundColor: severity === item.id ? 'rgba(139,92,246,0.18)' : 'var(--bg-surface, #0f1923)',
                          color: severity === item.id ? '#a78bfa' : 'var(--text-secondary)',
                          border: severity === item.id ? '1px solid rgba(139,92,246,0.38)' : '1px solid var(--card-border, #1e293b)',
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>What happened?</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    maxLength={4000}
                    placeholder="Example: I tried to add a build issue on mobile and the save button did nothing."
                    className="w-full rounded-xl px-4 py-3 text-sm resize-none outline-none"
                    style={{
                      backgroundColor: 'var(--input-bg, #0d1117)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--card-border, #1e293b)',
                    }}
                  />
                  <div className="flex justify-between mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                    <span>Page: {currentTab || 'unknown'}</span>
                    <span>{message.length}/4000</span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>Screenshot (optional)</label>
                  {screenshot ? (
                    <div className="flex items-center gap-3">
                      <img
                        src={screenshot}
                        alt="Captured screenshot preview"
                        className="rounded-lg border"
                        style={{ maxHeight: '90px', borderColor: 'var(--card-border, #1e293b)' }}
                      />
                      <button
                        type="button"
                        onClick={() => setScreenshot(null)}
                        className="px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                        style={{ color: '#fca5a5', backgroundColor: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.25)' }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Remove
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleCaptureScreenshot}
                      disabled={screenshotting}
                      className="px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60"
                      style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--bg-surface, #0f1923)', border: '1px solid var(--card-border, #1e293b)' }}
                    >
                      <Camera className="w-3.5 h-3.5" />
                      {screenshotting ? 'Capturing…' : 'Capture screenshot'}
                    </button>
                  )}
                </div>

                {error && (
                  <div className="px-3 py-2 rounded-lg text-sm" style={{ color: '#fca5a5', backgroundColor: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}>
                    {error}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={handleClose} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ color: 'var(--text-muted)', border: '1px solid var(--card-border, #1e293b)' }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting} className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-60" style={{ backgroundColor: 'var(--accent-primary, #2dd4bf)', color: 'var(--bg-base, #0a0f14)' }}>
                    <Send className="w-4 h-4" />
                    {submitting ? 'Sending...' : 'Send feedback'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
